import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/stores/auth';
import { toast } from '@/components/ui/use-toast';

function Ic({ type, size = 20, color = 'var(--soleil-gold)' }: { type: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const icons: Record<string, React.ReactNode> = {
    sun: <svg {...p}><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>,
  };
  return <>{icons[type] || null}</>;
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '14px 16px', background: 'var(--soleil-cream)',
  border: '1.5px solid var(--soleil-sand)', borderRadius: 11,
  color: 'var(--soleil-ink)', fontFamily: S.font, fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

export default function SoleilRegisterPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      const result = await register({ name: formData.name, email: formData.email, password: formData.password, phone: formData.phone });
      if ((result as any)?.requiresVerification) {
        navigate(`/check-email?email=${encodeURIComponent(formData.email)}`);
      } else {
        toast({ title: 'Success', description: 'Account created!' });
        navigate('/dashboard');
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '86vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--soleil-cream)', padding: '40px 24px' }}>
      <div style={{ maxWidth: 480, width: '100%', background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 22, overflow: 'hidden', boxShadow: '0 12px 48px rgba(46,42,38,0.08)' }}>
        {/* Top accent bar */}
        <div style={{ height: 3, background: 'linear-gradient(90deg, var(--soleil-gold), var(--soleil-sienna))' }} />

        <div style={{ padding: '40px 36px' }}>
          {/* Logo */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Ic type="sun" size={24} color="var(--soleil-dark)" />
            </div>
          </div>

          <h2 style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 600, color: 'var(--soleil-ink)', textAlign: 'center', marginBottom: 6 }}>
            {t('auth.register.title')}
          </h2>
          <p style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)', textAlign: 'center', marginBottom: 32 }}>
            {isRTL ? 'أنشئ حسابك وابدأ التعلم' : 'Create your account and start learning'}
          </p>

          <form onSubmit={handleSubmit}>
            {([
              [t('auth.register.name'), 'name', 'text', 'Your full name', true],
              [t('auth.register.email'), 'email', 'email', 'name@example.com', true],
              [t('auth.register.phone'), 'phone', 'tel', '+964 7XX XXX XXXX', false],
            ] as [string, string, string, string, boolean][]).map(([label, key, type, placeholder, req]) => (
              <div key={key} style={{ marginBottom: 16 }}>
                <label style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: 'var(--soleil-sub)', display: 'block', marginBottom: 7 }}>{label}</label>
                <input
                  type={type}
                  placeholder={placeholder}
                  value={(formData as any)[key]}
                  onChange={(e) => setFormData({ ...formData, [key]: key === 'phone' ? e.target.value.replace(/[^0-9+]/g, '') : e.target.value })}
                  required={req}
                  style={inputStyle}
                />
              </div>
            ))}

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: 'var(--soleil-sub)', display: 'block', marginBottom: 7 }}>{t('auth.register.password')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 8 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--soleil-mut)' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: 'var(--soleil-sub)', display: 'block', marginBottom: 7 }}>{t('auth.register.confirmPassword')}</label>
              <input
                type="password"
                placeholder="Confirm password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                style={inputStyle}
              />
            </div>

            <button type="submit" disabled={isLoading} style={{
              width: '100%', padding: 15, fontFamily: S.font, fontSize: 15, fontWeight: 600,
              color: 'var(--soleil-dark)',
              background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
              border: 'none', borderRadius: 11, cursor: isLoading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 16px rgba(184,148,58,0.2)', opacity: isLoading ? 0.7 : 1,
            }}>
              {isLoading ? 'Creating...' : t('auth.register.submit')}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 22, fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)' }}>
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" style={{ color: 'var(--soleil-sienna)', fontWeight: 600, textDecoration: 'none' }}>
              {t('auth.register.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
